import React, { useState, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, Paper, TextField, InputAdornment, Chip, Box, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const statusColors = {
  COMPLETED: { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' },
  FAILED: { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
  TIMED_OUT: { bg: '#ffedd5', color: '#9a3412', border: '#fdba74' },
  UPLOADED: { bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd' },
  PROCESSING: { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
  PENDING: { bg: '#f1f5f9', color: '#64748b', border: '#cbd5e1' },
  PARTIAL: { bg: '#ffedd5', color: '#9a3412', border: '#fdba74' },
  UPLOADING: { bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd' },
};

export function StatusBadge({ status }) {
  const style = statusColors[status] || statusColors.PENDING;
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        backgroundColor: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        fontWeight: 600,
        fontSize: '0.7rem',
        height: 24,
      }}
    />
  );
}

export function ScoreChange({ before, after }) {
  if (before == null || after == null) return <Typography variant="body2" color="text.secondary">-</Typography>;
  const change = Math.round((after - before) * 10) / 10;
  const color = change > 0 ? '#059669' : change < 0 ? '#dc2626' : '#64748b';
  const prefix = change > 0 ? '+' : '';
  return (
    <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
      {prefix}{change}%
    </Typography>
  );
}

function formatFileSize(bytes) {
  if (!bytes) return '-';
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

function descendingComparator(a, b, orderBy) {
  const aVal = a[orderBy] ?? '';
  const bVal = b[orderBy] ?? '';
  if (bVal < aVal) return -1;
  if (bVal > aVal) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export default function BatchResultsTable({ columns, rows, defaultSort, defaultOrder = 'asc', defaultComparator, searchPlaceholder }) {
  const [order, setOrder] = useState(defaultOrder);
  const [orderBy, setOrderBy] = useState(defaultSort || (columns[0]?.id ?? ''));
  const [userSorted, setUserSorted] = useState(false);
  const [filter, setFilter] = useState('');

  const handleSort = (columnId) => {
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
    setUserSorted(true);
  };

  const filteredRows = useMemo(() => {
    if (!filter) return rows;
    const lc = filter.toLowerCase();
    return rows.filter(row =>
      columns.some(col => {
        const val = row[col.id];
        if (val == null) return false;
        return String(val).toLowerCase().includes(lc);
      })
    );
  }, [rows, filter, columns]);

  const sortedRows = useMemo(() => {
    if (!userSorted && defaultComparator) {
      return [...filteredRows].sort(defaultComparator);
    }
    return [...filteredRows].sort(getComparator(order, orderBy));
  }, [filteredRows, order, orderBy, userSorted, defaultComparator]);

  return (
    <Box>
      <TextField
        size="small"
        placeholder={searchPlaceholder || 'Filter...'}
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          },
        }}
        sx={{ mb: 2, width: { xs: '100%', sm: 350 } }}
      />
      <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, overflow: 'auto' }}>
        <Table size="small" sx={{ minWidth: 'max-content' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f8fafc' }}>
              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align || 'left'}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                    borderBottom: '2px solid #e2e8f0',
                  }}
                  sortDirection={orderBy === col.id ? order : false}
                >
                  {col.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : 'asc'}
                      onClick={() => handleSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {filter ? 'No matching results' : 'No data available'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row, index) => (
                <TableRow
                  key={row._key || index}
                  hover
                  sx={{ '&:last-child td': { borderBottom: 0 } }}
                >
                  {columns.map((col) => (
                    <TableCell key={col.id} align={col.align || 'left'} sx={{ fontSize: '0.875rem' }}>
                      {col.render ? col.render(row[col.id], row) : row[col.id]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {filteredRows.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Showing {sortedRows.length} of {rows.length} {rows.length === 1 ? 'item' : 'items'}
        </Typography>
      )}
    </Box>
  );
}

BatchResultsTable.formatFileSize = formatFileSize;
